import { useEffect, useState } from 'react';
import { FileText, Lock, X } from 'lucide-react';

interface PrfAccessReasonModalProps {
  open: boolean;
  prfId: string;
  username: string;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  busy?: boolean;
  error?: string | null;
}

export default function PrfAccessReasonModal({
  open,
  prfId,
  username,
  onCancel,
  onConfirm,
  busy = false,
  error = null,
}: PrfAccessReasonModalProps) {
  const [reason, setReason] = useState('');
  const [openedAt] = useState(() => new Date());

  useEffect(() => {
    if (open) setReason('');
  }, [open, prfId]);

  if (!open) return null;

  const canSubmit = reason.trim().length >= 5 && !busy;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="flex items-center gap-1.5 text-sm font-bold">
              <Lock className="w-4 h-4 text-primary" />
              Justify PRF access
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{prfId}</p>
          </div>
          <button type="button" onClick={onCancel} disabled={busy} className="p-1.5 rounded-lg hover:bg-accent">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Patient Report Forms are confidential. Access is logged and will appear on any printed copy.
          </p>

          <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Username</span>
              <span className="ml-auto font-mono font-semibold">{username || '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground pl-5">Date / time</span>
              <span className="ml-auto font-mono">{openedAt.toLocaleString()}</span>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="prf-access-reason" className="text-[10px] uppercase text-muted-foreground">
              Reason for accessing this PRF *
            </label>
            <textarea
              id="prf-access-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="e.g. Clinical follow-up / insurance disclosure review / complaint investigation…"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-xs resize-none outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            <p className="text-[10px] text-muted-foreground">Minimum 5 characters. This justification is stored permanently against the PRF.</p>
          </div>

          {error && (
            <div className="text-[11px] text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold border border-border text-muted-foreground hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => onConfirm(reason.trim())}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {busy ? 'Recording…' : 'Open PRF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function storePrfAccessSession(prfId: string, access: { id: string; reason: string; accessedAt: string; username: string }) {
  try {
    sessionStorage.setItem(`c2_prf_access_${prfId}`, JSON.stringify(access));
  } catch {
    /* ignore */
  }
}

export function getPrfAccessSession(prfId: string): { id: string; reason: string; accessedAt: string; username: string } | null {
  try {
    const raw = sessionStorage.getItem(`c2_prf_access_${prfId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearPrfAccessSession(prfId: string) {
  try {
    sessionStorage.removeItem(`c2_prf_access_${prfId}`);
  } catch {
    /* ignore */
  }
}
